import { useCallback, useMemo } from 'react';
import { useMessage } from '@/shared/hooks/useMessage';
import { useGetPendingRequestsToApprove } from '../../api';
import { useQuery } from '@tanstack/react-query';
import { customInstance } from '@/shared/api/mutator/custom-instance';
import type { ActionFlowRequestDTO, ActionFlowRequestSearch } from '../types/request';

/**
 * Hook to fetch pending action flow requests
 *
 * @param searchParams - Search parameters excluding status (status will be set to PENDING)
 * @param options - Optional parameters to specify the source of pending requests
 * @returns Object containing pending requests data, loading state, error and refetch function
 */
export const usePendingRequests = (
    searchParams: Omit<ActionFlowRequestSearch, 'status'>,
    options?: { source?: 'action-flow' | 'loans' }
) => {
    const message = useMessage();
    const source = options?.source ?? 'action-flow';

    const {
        data: actionFlowResponse,
        isLoading: isActionFlowLoading,
        error: actionFlowError,
        refetch: refetchActionFlow
    } = useGetPendingRequestsToApprove({ search: searchParams }, {
        query: {
            enabled: source === 'action-flow',
            // @ts-ignore
            onError: (error) => {
                message.error('Failed to fetch pending requests');
            }
        }
    });

    const {
        data: loansResponse,
        isLoading: isLoansLoading,
        error: loansError,
        refetch: refetchLoans
    } = useQuery<ActionFlowRequestDTO[]>({
        queryKey: ['loan-pending-approvals', searchParams],
        queryFn: () => customInstance<ActionFlowRequestDTO[]>({
            url: '/api/loans/pending-approvals',
            method: 'GET'
        }),
        enabled: source === 'loans',
        retry: 1,
    });

    const filteredLoanRequests = useMemo(() => {
        const requests = loansResponse || [];
        const searchText = searchParams.q?.toString().trim().toLowerCase();
        const from = searchParams.from;
        const to = searchParams.to;

        return requests.filter((request) => {
            const matchesSearch = !searchText || [
                request.recordName,
                request.nodeName,
                request.scopeId,
                request.udf2,
                request.udf11,
                request.udf12,
            ].some((value) => value?.toString().toLowerCase().includes(searchText));

            const matchesDate = !from || !to || !request.scopeDate
                ? true
                : (() => {
                    const requestDate = new Date(request.scopeDate).getTime();
                    const start = new Date(from).setHours(0, 0, 0, 0);
                    const end = new Date(to).setHours(23, 59, 59, 999);
                    return requestDate >= start && requestDate <= end;
                })();

            return matchesSearch && matchesDate;
        });
    }, [loansResponse, searchParams.from, searchParams.to, searchParams.q]);

    return {
        requests: source === 'loans' ? filteredLoanRequests : (actionFlowResponse || []),
        isLoading: source === 'loans' ? isLoansLoading : isActionFlowLoading,
        error: source === 'loans' ? loansError : actionFlowError,
        refetch: source === 'loans' ? refetchLoans : refetchActionFlow
    };
};